import { Navigate, Outlet, Route } from 'react-router-dom';

import { ResponsiveLayout } from '@/components/widgets/t/ResponsiveLayout/ResponsiveLayout.layout';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComparePage } from '@/pages/shared/compare/Compare.page';
import { CompareListPage } from '@/pages/shared/compare/CompareList.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { ComplianceFrameworksPageWrapper } from '@/pages/shared/compliance-frameworks/components/ComplianceFrameworks.page-wrapper';
import { CopilotPage } from '@/pages/shared/copilot/Copilot.page';
import { DisclosurePage } from '@/pages/shared/disclosure/Disclosure.page';
import { HomePage } from '@/pages/shared/home/Home.page';
import { IndicatorsPage } from '@/pages/shared/indicators/Indicators.page';
import { KnowledgePage } from '@/pages/shared/knowledge/Knowledge.page';
import { LandBiodiversityPage } from '@/pages/shared/land/LandBiodiversity.page';
import { LandCarbonPage } from '@/pages/shared/land/LandCarbon.page';
import { LandSocialPage } from '@/pages/shared/land/LandSocial.page';
import { LandSoilPage } from '@/pages/shared/land/LandSoil.page';
import { LandWaterPage } from '@/pages/shared/land/LandWater.page';
import { MediaUploadPage } from '@/pages/shared/media-upload/MediaUpload.page';
import { PlotOverviewPage } from '@/pages/shared/media-upload/pages/PlotOverview.page';
import { OrganizationOnboardingPage } from '@/pages/shared/organization/pages/Onboarding.page';
import { InteractiveMapPage } from '@/pages/shared/plots/pages/InteractiveMap.page';
import { PlotGroupsPage } from '@/pages/shared/plots/PlotGroups.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { ContractsPage } from '@/pages/shared/portfolio/pages/contracts/Contracts.page';
import { ForecastsPage } from '@/pages/shared/portfolio/pages/forecasts/Forecasts.page';
import { PortfolioNaturalCapitalPage } from '@/pages/shared/portfolio/pages/NaturalCapital/PortfolioNaturalCapital.page';
import { ScenarioForecastsPage } from '@/pages/shared/portfolio/pages/scenarios/ScenarioForecasts.page';
import { StocksPage } from '@/pages/shared/portfolio/pages/stocks/Stocks.page';
import { UpliftUnitsPage } from '@/pages/shared/portfolio/pages/uplift/UpliftUnits.page';
import { PortfolioInvestmentsPage } from '@/pages/shared/portfolio/PortfolioInvestments.page';
import { DetailsPage } from '@/pages/shared/projects/pages/project/Details.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { WaterDetailPage as WaterDetailConservationPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/water/WaterDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { ReportsPage } from '@/pages/shared/reports/Reports.page';
import { UploadCoordinatesPage } from '@/pages/shared/staff-portal/pages/upload-plots/UploadCoordinates.page';
import { UploadPlotsPage } from '@/pages/shared/staff-portal/pages/upload-plots/UploadPlots.page';
import { InSituDataPage } from '@/pages/shared/uploads/pages/InSituData.page';
import { MediaMapsPage } from '@/pages/shared/uploads/pages/MediaMaps.page';
import { MediaMapsConfigurationPage } from '@/pages/shared/uploads/pages/pages/MediaMapsConfiguration.page';
import { UploadsPage } from '@/pages/shared/uploads/Uploads.page';
import { WorkstreamsPage } from '@/pages/shared/workstreams/Workstreams.page';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { HomePageProtection } from '@/routing/components/protections/home-page/HomePageProtection';
import { PlotGroupDetailsCleaner } from '@/routing/components/protections/PlotGroupDetailsCleaner/PlotGroupDetailsCleaner';
import { PlotsOnboardingProtection } from '@/routing/components/protections/plots-onboarding/PlotsOnboardingProtection';
import { RequireAccessiblePlot } from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { NoGroupByProtection } from '@/routing/components/protections/require-group-by/NoGroupByProtection';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { UploadsProtection } from '@/routing/components/protections/require-uploads/UploadsProtection';
import { SkipLoggedInUser } from '@/routing/components/protections/skip-logged-in-user/SkipLoggedInUser';
import { paths } from '@/routing/routing';

import { AccountPageWrapper } from '../../shared/account/components/Account.page-wrapper';
import { PlotPage } from '../../shared/projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { LandStewardCompleteAccountPage } from '../pages/complete-account/complete-account.page';
import { LandstewardInvestmentsPage } from '../pages/investments/Investments.page';
import { LandstewardEditPlotPage } from '../pages/plot/pages/edit/edit-plot.page';
import { LandstewardNewPlotPage } from '../pages/plot/pages/new/new-plot.page';
import { LandstewardPortfolioPage } from '../pages/portfolio/Portfolio.page';
import { ProjectsNewPage } from '../pages/projects/pages/new-project/NewProject.page';
import { LandStewardSignUpPage } from '../pages/sign-up/sign-up.page';
import { LandStewardSignedInRoute } from './components/protections/land-steward-signed-in-route/LandStewardSignedInRoute';

export const LandStewardPageRoutes = (
  <Route path='landsteward'>
    <Route element={<LandStewardSignedInRoute />}>
      <Route element={<ResponsiveLayout />}>
        <Route element={<PlotGroupDetailsCleaner />}>
          <Route path={paths.landSteward.account} element={<AccountPageWrapper />}>
            <Route index element={<AccountPage />} />
            <Route path={paths.landSteward.accountEditEmail} element={<AccountEditEmailPage />} />
            <Route path={paths.landSteward.accountEditName} element={<AccountEditNamePage />} />
            <Route path={paths.landSteward.accountEditPassword} element={<AccountEditPasswordPage />} />
            <Route path={paths.landSteward.accountLegal} element={<AccountLegalPage />} />
            <Route path={paths.landSteward.accountTeam}>
              <Route index element={<AccountTeamPage />} />
              <Route element={<RequireAdminRole />}>
                <Route path={paths.landSteward.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
                <Route path={paths.landSteward.accountTeamInvite} element={<AccountTeamInvitePage />} />
              </Route>
            </Route>
          </Route>

          <Route path={paths.landSteward.socialDetails} element={<LandSocialPage />} />

          <Route path={paths.landSteward.portfolio} element={<LandstewardPortfolioPage />} />

          <Route path={paths.landSteward.portfolioNaturalCapital} element={<PortfolioNaturalCapitalPage />} />
          <Route path={paths.landSteward.portfolioForecasts} element={<ForecastsPage />} />
          <Route path={paths.landSteward.portfolioInvestments} element={<PortfolioInvestmentsPage />} />
          <Route path={paths.landSteward.portfolioUnits} element={<UpliftUnitsPage />} />
          <Route path={paths.landSteward.portfolioStocks} element={<StocksPage />} />
          <Route path={paths.landSteward.portfolioContracts} element={<ContractsPage />} />

          <Route path={`${paths.landSteward.scenarioModels}/*`} element={<ScenarioForecastsPage />} />

          <Route path={`${paths.landSteward.disclosure}/*`} element={<DisclosurePage />} />

          <Route path={paths.landSteward.investments} element={<LandstewardInvestmentsPage />} />
          <Route element={<RequireSupportPlot />}>
            <Route path={paths.landSteward.plots} element={<PlotsPage />} />
            <Route element={<NoGroupByProtection />}>
              <Route path={paths.landSteward.groups} element={<PlotGroupsPage />} />
              <Route path={paths.landSteward.comparisons} element={<CompareListPage />} />
              <Route path={paths.landSteward.compare} element={<ComparePage />} />
            </Route>
          </Route>
          <Route index element={<Navigate to={paths.landSteward.projects} replace />} />

          <Route element={<NoGroupByProtection />}>
            <Route path={`${paths.landSteward.details}/*`} element={<DetailsPage />} />
            <Route path={paths.landSteward.biodiversityDetails} element={<LandBiodiversityPage />} />
            <Route path={paths.landSteward.soilDetails} element={<LandSoilPage />} />
            <Route path={paths.landSteward.carbonDetails} element={<LandCarbonPage />} />
            <Route path={paths.landSteward.waterDetails} element={<LandWaterPage />} />
          </Route>

          <Route path={paths.landSteward.projects}>
            <Route path={paths.landSteward.newProject} element={<ProjectsNewPage />} />
            <Route index element={<ProjectsPage />} />
            <Route path={paths.landSteward.projectsComplianceFrameworks} element={<ComplianceFrameworksPageWrapper />}>
              <Route index element={<ComplianceFrameworksPage />} />
            </Route>
            <Route element={<RequireAccessibleProject />}>
              <Route path={paths.landSteward.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
              <Route path={paths.landSteward.habitatIntactness} element={<HabitatIntactnessPages />} />
              <Route path={paths.landSteward.indicatorSpecies} element={<IndicatorSpeciesPages />} />
              <Route path={paths.landSteward.ndvi} element={<NDVIDetailPage />} />
              <Route path={`${paths.landSteward.projectDetails}/*`} element={<ProjectPage />} />
              <Route path={paths.landSteward.interactiveMap} element={<InteractiveMap />} />
              <Route path={paths.landSteward.projectDetailsWater} element={<WaterDetailConservationPage />} />
              <Route path={`${paths.landSteward.editPlot}/*`} element={<LandstewardEditPlotPage />} />
              <Route path={`${paths.landSteward.newPlot}/*`} element={<LandstewardNewPlotPage />} />
            </Route>
          </Route>
          <Route element={<RequireAccessiblePlot />}>
            <Route path={`${paths.landSteward.plot}/*`} element={<PlotPage />} />
            <Route path={paths.landSteward.plotInteractiveMap} element={<InteractiveMapPage />} />
          </Route>
          <Route path={`${paths.landSteward.editPlotOrg}/*`} element={<LandstewardEditPlotPage />} />

          <Route path={paths.landSteward.restrictedAccess} element={<RestrictedAccessPage />} />
          <Route path={paths.landSteward.indicators} element={<IndicatorsPage />} />
          <Route element={<HomePageProtection />}>
            <Route path={paths.landSteward.home} element={<HomePage />} />
          </Route>

          <Route path={paths.landSteward.contracts} element={<ContractsPage />} />
          <Route path={paths.landSteward.reports} element={<ReportsPage />} />

          <Route path={paths.landSteward.copilot} element={<CopilotPage />} />
          <Route path={paths.landSteward.knowledge} element={<KnowledgePage />} />
          <Route path={paths.landSteward.workstreams} element={<WorkstreamsPage />} />
          <Route element={<UploadsProtection />}>
            <Route path={paths.landSteward.uploads} element={<UploadsPage />} />
            <Route path={paths.landSteward.uploadsPlotOverview} element={<MediaUploadPage />} />
            <Route path={paths.landSteward.uploadsPlotOverviewPlot} element={<PlotOverviewPage />} />
            <Route path={paths.landSteward.insituDataUpload} element={<InSituDataPage />} />
            <Route path={paths.landSteward.mediaMaps} element={<MediaMapsPage />} />
            <Route path={paths.landSteward.mediaMapsConfiguration} element={<MediaMapsConfigurationPage />} />
          </Route>
          <Route element={<PlotsOnboardingProtection />}>
            <Route path={paths.landSteward.plotUpload} element={<UploadPlotsPage />} />
            <Route path={paths.landSteward.plotCoordinateUpload} element={<UploadCoordinatesPage />} />
          </Route>
        </Route>
        <Route path={paths.landSteward.organizationOnboarding} element={<OrganizationOnboardingPage />} />
      </Route>
    </Route>
    <Route
      element={
        <SkipLoggedInUser>
          <Outlet />
        </SkipLoggedInUser>
      }
    >
      <Route path={paths.landSteward.completeAccount} element={<LandStewardCompleteAccountPage />} />
      <Route path={paths.landSteward.signUp} element={<LandStewardSignUpPage />} />
    </Route>
  </Route>
);
